import {
  TextAreaAi,
  useInterruptChat,
  useSocket,
  useStartNewChat,
  useTextChat,
} from '@mint-lib/ai-chat';
import { cn } from '@mint-lib/tw-utils';
import type { Chat } from '@mint-sdk/api-ai-assistant';

import { usePromptMetadataGenerator } from '../../../features/MetadataCollector/useMetadataCollector.js';
import useAgenticChat from '../../../hooks/useAgenticChat.js';
import useAgenticModal from '../../../hooks/useAgenticModal.js';

type Props = {
  isFullScreen: boolean;
};

const AgenticAiChatInput = ({ isFullScreen }: Props) => {
  const { generateCustomTemplate, generateCustomMetadata } =
    usePromptMetadataGenerator();
  const { setChatSessionId } = useAgenticChat();
  const { currentPathname } = useAgenticModal();
  const { startNewChat } = useStartNewChat();
  const { chatId } = useSocket();
  const { isAiProcessing: isAiProcessingText, sendMessage } = useTextChat();

  const {
    setIsInterrupted,
    onInterruptReset,
    isInterruptSuccess,
    onInterrupt,
    isInterruptPending,
  } = useInterruptChat(chatId);

  const metadataTemplate = generateCustomTemplate(currentPathname);
  const customMetadata = generateCustomMetadata(currentPathname);

  const handleStartNewChat = async (
    text: string,
    metadata?: Chat.UserMetadataSchemaType,
  ) => {
    const mergedMetadata =
      customMetadata || metadata
        ? {
            ...customMetadata,
            ...metadata,
          }
        : undefined;

    const chat = await startNewChat(metadataTemplate, {
      text,
      metadata: mergedMetadata,
    });
    setChatSessionId(chat.chatSessionId);
  };

  const handleSendQuestion = (
    message: string,
    metadata?: Chat.UserMetadataSchemaType,
  ) => {
    const trimmedPrompt = message.trim();
    if (!trimmedPrompt) return;

    const mergedMetadata =
      customMetadata || metadata
        ? {
            ...customMetadata,
            ...metadata,
          }
        : undefined;

    if (chatId) {
      sendMessage({
        text: trimmedPrompt,
        metadata: mergedMetadata,
      });
    } else {
      handleStartNewChat(trimmedPrompt, metadata);
    }
    setIsInterrupted(false);
    onInterruptReset();
  };

  return (
    <div className="flex w-full flex-col items-center">
      <div
        className={cn(
          'flex flex-col items-center gap-1',
          isFullScreen ? 'mb-2 w-full' : 'w-full',
        )}
      >
        <TextAreaAi
          key={chatId}
          handleSendQuestion={handleSendQuestion}
          isReadOnlyAccess={false}
          isDisabled={isAiProcessingText}
          isInterruptible={isAiProcessingText}
          isInterruptPending={isInterruptPending}
          isInterruptSuccess={isInterruptSuccess}
          onInterrupt={onInterrupt}
          classNames="w-full"
          chatId={chatId}
          withFileSelection
          withScheduleChat
        />
      </div>
    </div>
  );
};

export default AgenticAiChatInput;
